import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { UserAvatarIcon } from "@/assets/icons/UserAvatarIcon";
import { DownArrowIcon } from "@/assets/icons/DownArrowIcon";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { useAuth } from "@/shared/providers/AuthProvider";
import SubMenu from "./SubMenu";
import { MEGA_MENU_IDS, NavMenuProps, NavItem } from "./types";

const NavMenu: React.FC<NavMenuProps> = ({
  title,
  menuId,
  items,
  isUserMenu = false,
  openMenu,
  isRouteActive,
  onToggle,
  onClose,
}) => {
  const { hasPermission, hasPermissions } = usePermissions();
  const { user } = useAuth();
  const isMegaMenu = MEGA_MENU_IDS.has(menuId);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [smCaretLeft, setSmCaretLeft] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * handleNavLinkClick – called by every internal <Link> in the menu.
   *
   * When the user is already on the target path (same-route click), we
   * invalidate ALL active React Query queries so every page that uses
   * React Query will automatically refetch its data.  This is the single
   * common place for the "refresh on same-route nav" behaviour – no
   * per-page wiring is required.
   */
  const handleNavLinkClick = (
    e: React.MouseEvent,
    targetPath: string,
    closeMenu = true,
  ) => {
    if (location.pathname === targetPath) {
      e.preventDefault();
      // Give the user a "fresh start" — same experience as navigating to the
      // page for the very first time:
      //   1. Remove all cached query data so queries run with no stale data.
      //   2. Navigate to the bare path (dropping all search params / filters).
      //   3. Pass a unique _mountKey in state so the Layout can key the page
      //      content and force a full remount, resetting all local React state
      //      (filters, sort, pagination, etc.) back to defaults.
      queryClient.removeQueries();
      navigate(targetPath, {
        replace: true,
        state: { _mountKey: Date.now() },
      });
    }
    if (closeMenu) {
      onClose();
    }
  };

  const getMenuItems = () =>
    Array.from(
      menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"], .submenu-container > button') || []
    );

  useEffect(() => {
    if (openMenu !== menuId) {
      setFocusedIndex(null);
      setSmCaretLeft(null);
      return;
    }
    if (!isMegaMenu) return;

    // At SM (600–1023px) the mega menu switches to position:fixed with right:8px.
    // The ::before caret uses left:var(--mega-arrow-left) relative to the panel's
    // left edge. When the panel is fixed to the right, we must recalculate the
    // caret left so it points at the center of the trigger button.
    const isSmBreakpoint = window.innerWidth >= 600 && window.innerWidth <= 1023;
    if (!isSmBreakpoint) {
      setSmCaretLeft(null);
      return;
    }

    // Use rAF so the menu panel has been painted and has its final dimensions.
    const raf = requestAnimationFrame(() => {
      const trigger = triggerRef.current;
      const panel = menuRef.current;
      if (!trigger || !panel) return;

      const triggerRect = trigger.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      // Center of trigger relative to the panel's left edge.
      const caretLeft = triggerRect.left + triggerRect.width / 2 - panelRect.left;
      setSmCaretLeft(`${Math.max(16, caretLeft)}px`);
    });

    return () => cancelAnimationFrame(raf);
  }, [openMenu, menuId, isMegaMenu]);

  useEffect(() => {
    if (openMenu === menuId && focusedIndex !== null) {
      const menuItems = getMenuItems();
      if (menuItems.length === 0) {
        return;
      }

      const normalizedIndex = focusedIndex < 0 ? menuItems.length - 1 : focusedIndex % menuItems.length;
      menuItems[normalizedIndex]?.focus();
    }
  }, [focusedIndex, openMenu, menuId]);

  const filterItemsByPermission = (items: NavItem[]): NavItem[] => {
    return items
      .filter((item) => {
        // Check user type restriction first
        if (item.allowedUserTypes && item.allowedUserTypes.length > 0) {
          const userType = user?.subcontractor ?? 0; // Default to Normal (0) if not set
          if (!item.allowedUserTypes.includes(userType)) {
            return false;
          }
        }
        
        // Check permissions
        if (!item.permission && !item.permissions) {
          return true;
        }
        if (item.permission) {
          return item.allowWithoutPermission || hasPermission(item.permission);
        }
        if (item.permissions) {
          return hasPermissions(item.permissions, false);
        }
        return true;
      })
      .map((item) => {
        const filteredSubmenu = item.submenu ? filterItemsByPermission(item.submenu) : undefined;
        return {
          ...item,
          ...(filteredSubmenu !== undefined && { submenu: filteredSubmenu }),
        };
      })
      .filter((item) => {
        if (item.submenu && item.submenu.length === 0) {
          return false;
        }
        return true;
      });
  };

  const filteredItems = filterItemsByPermission(items);

  if (filteredItems.length === 0 && !isUserMenu) {
    return null;
  }

  const columnCount = filteredItems.length;
  const gridColumns = Array(columnCount).fill("minmax(min-content, 1fr)").join(" ");
  const megaMenuWidth = "auto";

  const renderMenuLabel = (item: NavItem, compact = false) => {
    const Icon = item.icon;

    return (
      <span className={`nav-item-label ${compact ? "nav-item-label-compact" : ""}`}>
      {Icon && (
        <span className="nav-item-icon" aria-hidden="true">
          <Icon size={16} />
        </span>
      )}
      <span className="nav-item-text">{item.label}</span>
      {item.external && (
        <ExternalLink size={13} className="nav-item-external" aria-hidden="true" />
      )}
    </span>
    );
  };

  const moveFocus = (direction: "next" | "prev") => {
    const menuItems = getMenuItems();
    if (menuItems.length === 0) {
      return;
    }

    const activeIndex = menuItems.findIndex((item) => item === document.activeElement);
    const fallbackIndex = direction === "next" ? -1 : 0;
    const currentIndex = activeIndex >= 0 ? activeIndex : (focusedIndex ?? fallbackIndex);
    const nextIndex =
      direction === "next"
        ? (currentIndex + 1) % menuItems.length
        : (currentIndex - 1 + menuItems.length) % menuItems.length;

    setFocusedIndex(nextIndex);
    menuItems[nextIndex]?.focus();
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {

    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault();
        onToggle(menuId);
        if (openMenu !== menuId) {
          setFocusedIndex(0);
        }
        break;
      case "Escape":
        if (openMenu === menuId) {
          onClose();
        }
        break;
      case "ArrowDown":
        event.preventDefault();
        if (openMenu !== menuId) {
          onToggle(menuId);
          setFocusedIndex(0);
        } else {
          moveFocus("next");
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (openMenu !== menuId) {
          onToggle(menuId);
          setFocusedIndex(-1);
        } else {
          moveFocus("prev");
        }
        break;
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        onClose();
        break;
      case "ArrowDown":
        event.preventDefault();
        moveFocus("next");
        break;
      case "ArrowUp":
        event.preventDefault();
        moveFocus("prev");
        break;
      case "Home": {
        const menuItems = getMenuItems();
        if (menuItems.length === 0) {
          break;
        }
        event.preventDefault();
        setFocusedIndex(0);
        menuItems[0]?.focus();
        break;
      }
      case "End": {
        const menuItems = getMenuItems();
        if (menuItems.length === 0) {
          break;
        }
        event.preventDefault();
        const lastIndex = menuItems.length - 1;
        setFocusedIndex(lastIndex);
        menuItems[lastIndex]?.focus();
        break;
      }
    }
  };

  return (
    <div className="navbar-menu-container">
      <button
        ref={triggerRef}
        onClick={() => onToggle(menuId)}
        onKeyDown={handleTriggerKeyDown}
        aria-expanded={openMenu === menuId}
        aria-haspopup="menu"
        aria-controls={`${menuId}-menu`}
        // Don't append "menu" here — aria-haspopup="menu" already makes screen
        // readers announce that word themselves, so adding it to the label too
        // produced "Operations menu menu" / "User menu: Jane Doe menu".
        aria-label={isUserMenu ? `User: ${title}` : title}
        className={
          isUserMenu
            ? `navbar-user-menu-trigger font-normal ${openMenu === menuId ? "active" : ""}`
            : `nav-top-trigger flex items-center text-on-primary font-medium gap-1 px-4 ${openMenu === menuId || isRouteActive ? "active" : ""}`
        }
      >
        {isUserMenu && <UserAvatarIcon className="shrink-0" />}
        <span>{title}</span>
        <DownArrowIcon aria-hidden="true" />
      </button>
      {openMenu === menuId && (
      <div
        ref={menuRef}
        id={`${menuId}-menu`}
        onKeyDown={handleMenuKeyDown}
        className={`navbar-menu open navbar-menu-${menuId} ${isMegaMenu ? "mega" : ""} ${menuId === "operations" ? "operations-mega-menu" : ""} ${menuId === "admin" ? "admin-mega-menu" : ""} ${menuId === "tools" ? "tools-mega-menu" : ""} ${menuId === "system" ? "system-mega-menu" : ""}`}
        role="menu"
        aria-label={`${title} menu items`}
        tabIndex={-1}
        style={{
          width: megaMenuWidth,
          ...(smCaretLeft ? { "--mega-arrow-left": smCaretLeft } as React.CSSProperties : {}),
        }}
      >
          {isMegaMenu ? (
            <div className="mega-menu-scroll-wrapper">
            <div
              className="mega-menu-grid"
              style={{ gridTemplateColumns: gridColumns }}
            >
              {filteredItems.map((item) => (
                <div key={item.key} className="min-w-0">
                  <div className="mega-menu-section-title">{item.label}</div>
                  <div className="flex flex-col gap-0">
                    {item.submenu?.map((subItem) => (
                      subItem.external ? (
                        <a
                          key={subItem.key}
                          href={subItem.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={onClose}
                          className="mega-menu-link"
                          role="menuitem"
                        >
                          {renderMenuLabel(subItem)}
                        </a>
                      ) : (
                        <Link
                          key={subItem.key}
                          to={subItem.to}
                          onClick={(e) => handleNavLinkClick(e, subItem.to)}
                          className="mega-menu-link"
                          role="menuitem"
                        >
                          {renderMenuLabel(subItem)}
                        </Link>
                      )
                    ))}
                  </div>
                </div>
              ))}
            </div>
            </div>
          ) : (
            filteredItems.map((item) =>
                item.divider ? (
                  <div key={item.key} className="border-t border-border" />
                ) : item.versionInfo ? (
                  <div
                    key={item.key}
                    className="group relative text-sm select-none"
                    role="menuitem"
                    aria-label="Application version information"
                  >
                    {/* Visible row: FE version + optional Release notes link */}
                    <div className="flex items-center gap-2">
                      {item.frontendVersion && (
                        <span className="text-text-muted font-mono">{item.frontendVersion}</span>
                      )}
                      {item.releaseNotesUrl && (
                        <a
                          href={item.releaseNotesUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="release-notes-link hover:underline"
                        >
                          Release notes
                        </a>
                      )}
                    </div>
                    {/* Hover tooltip: full version details */}
                    {(item.frontendVersion || item.version || item.reactVersion) && (
                      <div
                        className="absolute top-full left-0 mt-1 hidden group-hover:block z-50 bg-surface border border-border rounded shadow-lg px-3 py-2 text-xs text-text whitespace-nowrap"
                        role="tooltip"
                      >
                        {item.frontendVersion && (
                          <div>Ecolane UI — {item.frontendVersion}</div>
                        )}
                        {item.version && (
                          <div>Backend — {item.version}</div>
                        )}
                        {item.reactVersion && (
                          <div>React — {item.reactVersion}</div>
                        )}
                      </div>
                    )}
                  </div>
                ) : item.submenu ? (
                <SubMenu
                  key={item.key}
                  title={item.label!}
                  closeParentMenu={onClose}
                  {...(item.testId ? { testId: item.testId } : {})}
                >
                  {item.submenu.map((subItem) =>
                    subItem.external ? (
                      <a
                        key={subItem.key}
                        href={subItem.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-4 py-2 text-sm text-text hover:bg-surface-alt"
                        role="menuitem"
                        data-testid={subItem.testId}
                      >
                        {renderMenuLabel(subItem, true)}
                      </a>
                    ) : subItem.onClick ? (
                      // onClick submenu items (e.g. Language, Theme) — render as button
                      // so the click handler fires correctly via SubMenu.createClickHandler
                      <button
                        key={subItem.key}
                        onClick={subItem.onClick}
                        className="w-full text-left block px-4 py-2 text-sm text-text hover:bg-surface-alt"
                        role="menuitem"
                        data-testid={subItem.testId}
                      >
                        {renderMenuLabel(subItem, true)}
                      </button>
                    ) : (
                      <Link
                        key={subItem.key}
                        to={subItem.to}
                        onClick={(e) => handleNavLinkClick(e, subItem.to, false)}
                        className="block px-4 py-2 text-sm text-text hover:bg-surface-alt"
                        role="menuitem"
                        data-testid={subItem.testId}
                      >
                        {renderMenuLabel(subItem, true)}
                      </Link>
                    )
                  )}
                </SubMenu>
              ) : item.onClick ? (
                <button
                  key={item.key}
                  onClick={() => {
                    item.onClick?.();
                    onClose();
                  }}
                  className={isUserMenu
                    ? (item.key === "user-signout" ? "text-error" : "")
                    : `block w-full text-left px-4 py-2 text-sm ${item.key === "user-signout" ? "text-error" : "text-text"} hover:bg-surface-alt`}
                  role="menuitem"
                  {...(item.key === "user-signout" ? { "data-signout": "true" } : {})}
                >
                  {renderMenuLabel(item, true)}
                </button>
              ) : (
                item.external ? (
                      <a
                        key={item.key}
                        href={item.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={isUserMenu ? "" : "block px-4 py-2 text-sm text-text hover:bg-surface-alt"}
                        role="menuitem"
                      >
                        {renderMenuLabel(item, true)}
                      </a>
                    ) : (
                    <Link
                      key={item.key}
                      to={item.to}
                      onClick={(e) => handleNavLinkClick(e, item.to)}
                      className={isUserMenu
                        ? (item.key === "user-signout" ? "text-error" : "")
                        : `block px-4 py-2 text-sm ${item.key === "user-signout" ? "text-error" : "text-text"} hover:bg-surface-alt`}
                      role="menuitem"
                      {...(item.key === "user-signout" ? { "data-signout": "true" } : {})}
                    >
                  {renderMenuLabel(item, true)}
                </Link>
                    )
              )
            )
          )}
        </div>
      )}
    </div>
  );
};

export default NavMenu;

